import { useState, useRef, useCallback, useEffect } from 'react';
import { textToSpeech } from '../services/speechApi';

/**
 * Custom hook for Chirp 3 HD TTS and audio recording.
 * TTS via backend Chirp 3 HD. Recording returns raw audioBlob
 * (no STT — audio is sent directly to Gemini 2.5 Flash for evaluation).
 */
export default function useSpeech() {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const objectUrlRef = useRef(null);
  const listeningTimerRef = useRef(null);
  const forceStopRef = useRef(false);
  const onCompleteRef = useRef(null);

  // Cleanup object URLs to prevent memory leaks
  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  /**
   * Speak text using backend Chirp 3 HD TTS.
   * Falls back to browser speechSynthesis if backend fails.
   */
  const speak = useCallback(async (text, options = {}) => {
    const { onEnd, onError, languageCode = 'en-US', voiceName } = options;

    try {
      console.log('🔊 [Chirp3] Speaking:', text.substring(0, 50) + '...');
      setIsSpeaking(true);

      const blob = await textToSpeech(text, languageCode, voiceName);
      revokeObjectUrl();
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;

      const audio = new Audio(url);
      audioRef.current = audio;

      audio.onended = () => {
        console.log('✓ [Chirp3] TTS ended');
        setIsSpeaking(false);
        audioRef.current = null;
        if (onEnd) onEnd();
      };

      audio.onerror = (err) => {
        console.error('[Chirp3] Audio playback error:', err);
        setIsSpeaking(false);
        audioRef.current = null;
        if (onError) onError(err);
        else if (onEnd) onEnd();
      };

      await audio.play();
    } catch (error) {
      console.warn('[Chirp3] Backend TTS failed, falling back to browser TTS:', error);
      setIsSpeaking(true);

      // Fallback to browser speechSynthesis
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 1;

        utterance.onend = () => {
          setIsSpeaking(false);
          if (onEnd) onEnd();
        };
        utterance.onerror = () => {
          setIsSpeaking(false);
          if (onError) onError(error);
          else if (onEnd) onEnd();
        };

        window.speechSynthesis.speak(utterance);
      } else {
        setIsSpeaking(false);
        if (onEnd) onEnd();
      }
    }
  }, [revokeObjectUrl]);

  /**
   * Stop any ongoing TTS playback.
   */
  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    // Also cancel browser TTS fallback
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  }, []);

  /**
   * Start recording audio from a mic stream.
   * Returns nothing — call stopListening() to get the audioBlob.
   */
  const startListening = useCallback((micStream) => {
    if (!micStream) {
      console.error('[Recording] No microphone stream provided');
      return;
    }

    // Extract audio-only stream from the mic stream
    const audioTracks = micStream.getAudioTracks();
    if (audioTracks.length === 0) {
      console.error('[Recording] No audio tracks in stream');
      return;
    }
    const audioStream = new MediaStream(audioTracks);

    // Pick a supported MIME type
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

    audioChunksRef.current = [];
    const recorder = new MediaRecorder(audioStream, { mimeType });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    recorder.start(1000); // collect data every second
    setIsListening(true);
    console.log('🎤 [Recording] Started');
  }, []);

  /**
   * Stop recording and return the raw audio Blob.
   * No STT — audio goes directly to Gemini for evaluation.
   */
  const stopListening = useCallback(() => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        setIsListening(false);
        resolve(null);
        return;
      }

      recorder.onstop = () => {
        setIsListening(false);
        console.log('🎤 [Recording] Stopped');

        const chunks = audioChunksRef.current;
        if (chunks.length === 0) {
          resolve(null);
          return;
        }

        const blob = new Blob(chunks, { type: recorder.mimeType });
        audioChunksRef.current = [];

        console.log(`✓ [Recording] Audio blob: ${(blob.size / 1024).toFixed(1)} KB`);
        resolve(blob);
      };

      recorder.stop();
    });
  }, []);

  /**
   * Timed listening window (10-60s) — matches the existing interview UX.
   * Records audio, then returns audioBlob when done.
   *
   * @param {MediaStream} micStream - Camera/mic stream with audio tracks
   * @param {Object} options
   * @param {number} options.minSeconds - Min recording time (default 10)
   * @param {number} options.maxSeconds - Max recording time (default 60)
   * @param {Function} options.onComplete - Called with audioBlob (Blob or null)
   * @param {Function} options.onTick - Called every second with {elapsed, remaining, allowStop}
   */
  const startListeningWindow = useCallback((micStream, options = {}) => {
    const {
      minSeconds = 10,
      maxSeconds = 60,
      onComplete,
      onTick,
    } = options;

    forceStopRef.current = false;
    onCompleteRef.current = onComplete;

    // Start recording
    startListening(micStream);

    const startedAt = Date.now();

    // Tick timer for countdown updates
    listeningTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      const remaining = Math.max(0, Math.ceil(maxSeconds - elapsed));
      const allowStop = elapsed >= minSeconds;

      if (onTick) {
        onTick({ elapsed, remaining, allowStop });
      }

      // Force stop at max time
      if (elapsed >= maxSeconds) {
        forceStopRef.current = true;
        finishListeningWindow();
      }
    }, 1000);
  }, [startListening]);

  /**
   * Finish the listening window — stop recording and call onComplete with audioBlob.
   */
  const finishListeningWindow = useCallback(async () => {
    if (listeningTimerRef.current) {
      clearInterval(listeningTimerRef.current);
      listeningTimerRef.current = null;
    }

    setIsProcessing(true);
    const audioBlob = await stopListening();
    setIsProcessing(false);

    if (onCompleteRef.current) {
      onCompleteRef.current(audioBlob);
      onCompleteRef.current = null;
    }
  }, [stopListening]);

  /**
   * Clean up all resources.
   */
  const cleanup = useCallback(() => {
    stopSpeaking();
    revokeObjectUrl();

    if (listeningTimerRef.current) {
      clearInterval(listeningTimerRef.current);
      listeningTimerRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
  }, [stopSpeaking, revokeObjectUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  return {
    isSpeaking,
    isListening,
    isProcessing,
    speak,
    stopSpeaking,
    startListening,
    stopListening,
    startListeningWindow,
    finishListeningWindow,
    cleanup,
  };
}
